import { createDeduplicate, memoize, range } from "./_common";
import { AxialPoint } from "./AxialPoint";
import { linearInterpolate } from "./_common";
import { PixelPoint } from "./PixelPoint";
import { Record, Set } from "immutable";
const CubePointCtor = Record({ type: 'ICubePoint', q: 0, r: 0, s: 0 });
export var CubePoint;
(function (CubePoint) {
    CubePoint.create = (pointOrQ, r, s) => CubePointCtor((typeof pointOrQ === 'number')
        ? ({ q: pointOrQ, r: r, s: s })
        : pointOrQ);
    // Arithmetic
    CubePoint.add = (p1, p2) => CubePoint.create({
        q: p1.q + p2.q,
        r: p1.r + p2.r,
        s: p1.s + p2.s,
    });
    CubePoint.negate = ({ q, r, s }) => CubePoint.create({ q: -q, r: -r, s: -s });
    CubePoint.subtract = (p1, p2) => CubePoint.add(p1, CubePoint.negate(p2));
    // Conversions
    CubePoint.toAxialPoint = ({ q, r }) => AxialPoint.create({ q, r });
    CubePoint.toOffsetPoint = (point) => AxialPoint.toOffsetPoint(CubePoint.toAxialPoint(point));
    CubePoint.toPixelPoint = (point) => AxialPoint.toPixelPoint(CubePoint.toAxialPoint(point));
    // Constraints
    CubePoint.round = ({ q: prevQ, r: prevR, s: prevS }) => {
        const q = Math.round(prevQ);
        const r = Math.round(prevR);
        const s = Math.round(prevS);
        const qDiff = Math.abs(q - prevQ);
        const rDiff = Math.abs(r - prevR);
        const sDiff = Math.abs(s - prevS);
        if (qDiff > rDiff && qDiff > sDiff) {
            return CubePoint.create({ q: -r - s, r, s });
        }
        else if (rDiff > sDiff) {
            return CubePoint.create({ q, r: -q - s, s });
        }
        return CubePoint.create({ q, r, s: -q - r });
    };
    // Spatiality
    CubePoint.getDistance = (p1, p2) => {
        const { q, r, s } = CubePoint.subtract(p1, p2);
        return (Math.abs(q) + Math.abs(r) + Math.abs(s)) / 2;
    };
    CubePoint.interpolateLinear = (p1, p2, percentile) => CubePoint.create({
        q: linearInterpolate(p1.q, p2.q, percentile),
        r: linearInterpolate(p1.r, p2.r, percentile),
        s: linearInterpolate(p1.s, p2.s, percentile),
    });
    // Aggregation
    CubePoint.allPointsWithinRange = memoize((center, distance) => `CubePoint.allPointsWithinRange:${CubePoint.toCacheKey(center)}:${distance}`, (center, distance) => {
        const qVectors = range({ start: -distance, endInclusive: distance });
        const axialVectors = qVectors.flatMap((q) => (range({
            start: Math.max(-distance, -q - distance),
            endInclusive: Math.min(distance, -q + distance),
        }).map((r) => AxialPoint.create({ q, r }))));
        return Set(axialVectors.map(axialVector => CubePoint.add(CubePoint.round(center), AxialPoint.toCubePoint(axialVector))));
    });
    CubePoint.toCacheKey = ({ q, r, s }, precision) => {
        const qKey = precision ? Math.round(q * Math.pow(10, precision)) : q;
        const rKey = precision ? Math.round(r * Math.pow(10, precision)) : r;
        const sKey = precision ? Math.round(s * Math.pow(10, precision)) : s;
        return `${qKey}:${rKey}:${sKey}`;
    };
    CubePoint.deduplicate = createDeduplicate(CubePoint.toCacheKey);
    const naivePointsAlongLine = memoize((p1, p2, opts = {}) => `CubePoint.naivePointsAlongLine:${CubePoint.toCacheKey(p1)}:${CubePoint.toCacheKey(p2)}:${opts.sampleRate}`, (p1, p2, opts = {}) => {
        const sampleRate = opts.sampleRate || 1;
        const distance = CubePoint.getDistance(p1, p2);
        const sampleCount = 1 + Math.ceil(distance) * sampleRate;
        const percentiles = range(sampleCount).map(idx => (idx / sampleCount));
        const interpolate = CubePoint.interpolateLinear.bind(null, p1, p2);
        return Set(percentiles.map(interpolate).map(CubePoint.round));
    });
    CubePoint.pointsAlongLine = memoize((p1, p2, opts = {}) => `CubePoint.pointsAlongLine:${CubePoint.toCacheKey(p1)}:${CubePoint.toCacheKey(p2)}:${opts.sampleRate}:${opts.lineWidth}`, (p1, p2, opts = {}) => {
        const pointsAlongLine = naivePointsAlongLine(p1, p2, opts);
        return (opts.lineWidth) ? pointsAlongLine.reduce((set, point) => set.union(CubePoint.allPointsWithinRange(point, opts.lineWidth - 1)), Set()) : pointsAlongLine;
    });
    CubePoint.pointsAlongPixelLine = (p1, p2, opts = {}) => (CubePoint.pointsAlongLine(PixelPoint.toRoundedCubePoint(p1), PixelPoint.toRoundedCubePoint(p2), opts));
})(CubePoint || (CubePoint = {}));
//# sourceMappingURL=CubePoint.js.map